import { useApi } from "../lib/useApi";
import {
  useStatusQuery,
  useGhlStatusQuery,
  useSitesQuery,
  useIngestStatusQuery,
  useCheckConnectionMutation,
} from "../data";
import { TopBar } from "../components/TopBar";
import { Hero } from "../components/Hero";
import { SystemPanel } from "../components/SystemPanel";
import { GhlAuthPanel } from "../components/GhlAuthPanel";
import { SitesPanel } from "../components/SitesPanel";
import { AddCustomerPanel } from "../components/AddCustomerPanel";
import { IngestPanel } from "../components/IngestPanel";
import { DiagnosticPanel } from "../components/DiagnosticPanel";
import { ApiRequestError } from "../types";

function envFromUrl(url: string | undefined): { label: string; kind: "prod" | "dev" | "other" } {
  if (!url) return { label: "dev", kind: "dev" };
  try {
    const host = new URL(url).host;
    if (/(^|\.)ghl\.mediaraq\.net$/i.test(host)) return { label: "prod", kind: "prod" };
    if (/(^|\.)(localhost|127\.0\.0\.1)(:|$)/i.test(host)) return { label: "dev", kind: "dev" };
    return { label: host, kind: "other" };
  } catch {
    return { label: "dev", kind: "dev" };
  }
}

export function Dashboard() {
  const api = useApi();
  const status = useStatusQuery(api);
  const ghl = useGhlStatusQuery(api);
  const sites = useSitesQuery(api);
  const ingest = useIngestStatusQuery(api);
  const checkConnection = useCheckConnectionMutation(api);
  const refreshIngest = ingest.refetch;

  const env = envFromUrl(status.data?.public_url);
  const isLoading = status.isLoading || ghl.isLoading || sites.isLoading || ingest.isLoading;
  const errored = Boolean(status.error && ghl.error && sites.error && ingest.error);
  const lastUpdated = status.dataUpdatedAt
    ? new Date(status.dataUpdatedAt).toISOString()
    : null;
  const accessError = [status.error, ghl.error, sites.error, ingest.error].find(
    (error) => error instanceof ApiRequestError && (error.status === 401 || error.status === 403),
  );

  if (accessError instanceof ApiRequestError) {
    return (
      <main className="auth-shell">
        <section className="auth-panel" aria-labelledby="accessTitle">
          <p className="eyebrow">GHL-Lite</p>
          <h1 id="accessTitle">Console access</h1>
          <p className="auth-copy">
            {accessError.status === 403
              ? "Your Clerk account is signed in but is not approved for this console."
              : "Your session expired or could not be verified. Sign in again to continue."}
          </p>
          <p className="panel-error">{accessError.message}</p>
        </section>
      </main>
    );
  }

  return (
    <main className="shell" aria-busy={isLoading}>
      <TopBar envLabel={env.label} envKind={env.kind} lastUpdated={lastUpdated} />

      <Hero
        system={status.data}
        ghl={ghl.data}
        sites={sites.data}
        loading={isLoading}
        errored={errored}
      />

      <section className="grid" aria-label="Detailed status panels">
        <SystemPanel
          status={status.data}
          isLoading={status.isLoading}
          error={status.error}
        />
        <GhlAuthPanel status={ghl.data} isLoading={ghl.isLoading} error={ghl.error} />
        <SitesPanel
          inventory={sites.data}
          isLoading={sites.isLoading}
          error={sites.error}
        />
        <AddCustomerPanel api={api} />
        <IngestPanel
          status={ingest.data}
          isLoading={ingest.isLoading}
          isRefreshing={ingest.isFetching && !ingest.isLoading}
          error={ingest.error}
          onRefresh={() => {
            void refreshIngest();
          }}
        />
        <DiagnosticPanel
          data={checkConnection.data}
          isPending={checkConnection.isPending}
          error={checkConnection.error}
        />
      </section>

      <footer className="footer">
        <span className="muted-meta">Read-only console. No secret material is shown.</span>
      </footer>
    </main>
  );
}
